import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import {
  Client,
  ClientsService,
  CoursesService,
  Distributor,
  DynamicFormComponent,
  PopUpService,
  DistributorsService,
  ImageConversionService
} from '../../core';
import { ClientsQuestionsService } from '../questions-services/clients-questions.service';
import { PlansService } from 'core/services/plans.service';

@Component({
  selector: 'lms-client-editor',
  templateUrl: './client-editor.component.html',
  styleUrls: ['./client-editor.component.scss']
})
export class ClientEditorComponent implements OnChanges, OnInit {
  @Input() client: Client;
  @Input() catalogueData: any;
  @Output() onSave = new EventEmitter<Client>();
  @ViewChild(DynamicFormComponent, { static: false }) public dynamicForm: DynamicFormComponent;
  public title: string;
  public questions: any[];
  public distributor: Distributor;

  constructor(
    public clientsService: ClientsService,
    public coursesService: CoursesService,
    public plansService: PlansService,
    public distributorsService: DistributorsService,
    public clientsQuestions: ClientsQuestionsService,
    public activatedRoute: ActivatedRoute,
    public router: Router,
    public popupService: PopUpService,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.activatedRoute.data.subscribe((data) => {
      this.distributor = data.distributor ? data.distributor : data.client ? data.client.partner : null;
    });
    this.title = this.client ? 'clients.edit' : 'clients.create';
    if (!this.questions) this.setQuestions();
  }

  ngOnChanges() {
    this.setQuestions();
    this.cd.detectChanges();
  }

  setQuestions() {
    if (!this.distributor && this.client && this.client.distributorId) {
      if (this.client.partner) {
        this.distributor = this.client.partner;
      } else {
        return this.distributorsService.getDistributor(this.client.distributorId).subscribe((distributor) => {
          this.distributor = distributor;
          this.questions = this.clientsQuestions.getQuestions(this.client, this.distributor, this.catalogueData);
        });
      }
    }
    this.questions = this.clientsQuestions.getQuestions(this.client, this.distributor, this.catalogueData);
  }

  async submit(clientData: any) {
    const n6Options = {
      inactive: { enabled: false, includeSurvey: false },
      diploma: { enabled: true, includeSurvey: false },
      survey: { enabled: true, includeSurvey: true }
    };
    const editedCatalogueData = clientData['catalogueData'];
    editedCatalogueData.configuration.shouldSendDiploma = JSON.parse(
      editedCatalogueData.configuration.shouldSendDiploma
    );
    delete clientData['catalogueData'];

    clientData.ranking = {
      showRanking: clientData['rankingConfig']['config'] === 'showRanking',
      showAlias: clientData['rankingConfig']['config'] === 'showAlias',
      obfuscateRanking: clientData['rankingConfig']['config'] === 'obfuscateRanking'
    };
    clientData.gamification = { enabled: clientData['rankingConfig']['showGamification'] || false };
    clientData.referrals = clientData.notificationsCfg['referrals'];
    clientData.notificationsCfg.n3.weekPeriodicity = clientData.notificationsCfg['weekPeriodicity'];
    clientData.notificationsCfg.n6 = n6Options[clientData.notificationsCfg['fundaeDiploma']];

    delete clientData['rankingConfig'];
    delete clientData.notificationsCfg['referrals'];
    delete clientData.notificationsCfg['weekPeriodicity'];
    delete clientData.notificationsCfg['allNotifications'];
    delete clientData.notificationsCfg['fundaeDiploma'];

    if (clientData.logo) {
      delete clientData.logo.saved;

      if (clientData.logo.image) {
        clientData.logo.image = await ImageConversionService.compressBase64StringImage(clientData.logo.image, {
          quality: 0.85,
          height: 800
        });
      }
    }
    if (this.client) {
      await this.onUpdate(clientData, editedCatalogueData);
    } else {
      await this.onCreate(clientData, editedCatalogueData);
    }
  }

  async onCreate(clientData: Object, editedCatalogueData: Object) {
    try {
      const client = await this.clientsService.create(clientData);
      const catalogue = await this.coursesService.loadClientCatalogueCache(client.id, true);
      await this.coursesService.updateCatalogue(catalogue.id, editedCatalogueData);

      this.onSave.emit(client);
      this.endSubmit(null);
    } catch (err) {
      this.endSubmit(err);
    }
  }

  async onUpdate(updatedData: Object, editedCatalogueData: Object) {
    const client: Client = Object.assign({}, this.client, updatedData);
    try {
      const updatedClient = await this.clientsService.update(client, true);
      await this.coursesService.updateCatalogue(this.catalogueData.id, editedCatalogueData);

      this.onSave.emit({ ...this.client, ...updatedClient });
      this.endSubmit(null);
    } catch (err) {
      this.endSubmit(err);
    }
  }

  endSubmit(value) {
    this.dynamicForm.submitted(value);
  }
}
